if (!customElements.get("fabric-samples-page")) {
  class FabricSamplesPage extends HTMLElement {
    constructor() {
      super();
      this.initialized = false;
      this.scrollFrame = null;
      this.handleRootChange = (event) => {
        const target = event.target instanceof Element ? event.target : null;
        const input = target?.closest('.fabrics--full input[name="fabrics"]');
        if (input && this.contains(input)) this.handleInputChange(input);
      };
      this.handleRootClick = (event) => this.handleClick(event);
      this.handleViewportChange = () => this.requestTabUpdate();
      this.handleDrawerOpen = (event) => this.updateFilterExpanded(event, true);
      this.handleDrawerClosed = (event) => this.updateFilterExpanded(event, false);
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.limit = Number(this.dataset.selectionLimit) || 5;
      this.inputs = [...this.querySelectorAll('.fabrics--full input[name="fabrics"]')];
      this.summary = this.querySelector("[data-fabrics-summary]");
      this.summaryList = this.querySelector("[data-fabrics-summary-list]");
      this.summaryTemplate = this.querySelector("[data-fabrics-summary-row-template]");
      this.mobileTray = this.querySelector("[data-fabrics-mobile-tray]");
      this.mobileSummaryList = this.querySelector("[data-fabrics-mobile-summary-list]");
      this.mobileSummaryTemplate = this.querySelector(
        "[data-fabrics-mobile-summary-slot-template]",
      );
      this.mobileEmptyTemplate = this.querySelector(
        "[data-fabrics-mobile-empty-slot-template]",
      );
      this.promo = this.querySelector("[data-fabrics-promo]");
      this.status = this.querySelector("[data-fabrics-status]");
      this.filterButton = this.querySelector('[aria-controls="drawer-filters"]');
      this.tabs = [...this.querySelectorAll("[data-fabrics-tab]")];
      this.categories = [...this.querySelectorAll("[data-fabrics-category]")];

      this.addEventListener("change", this.handleRootChange);
      this.addEventListener("click", this.handleRootClick);
      window.addEventListener("scroll", this.handleViewportChange, { passive: true });
      window.addEventListener("resize", this.handleViewportChange, { passive: true });
      window.addEventListener("drawerOpen", this.handleDrawerOpen);
      window.addEventListener("drawerClosed", this.handleDrawerClosed);

      this.syncDrawerOptions();
      this.renderSummary();
      this.updateActiveTab();
    }

    disconnectedCallback() {
      this.removeEventListener("change", this.handleRootChange);
      this.removeEventListener("click", this.handleRootClick);
      window.removeEventListener("scroll", this.handleViewportChange);
      window.removeEventListener("resize", this.handleViewportChange);
      window.removeEventListener("drawerOpen", this.handleDrawerOpen);
      window.removeEventListener("drawerClosed", this.handleDrawerClosed);
      if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = null;
      this.initialized = false;
    }

    handleClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const removeButton = target.closest("[data-summary-remove]");

      if (removeButton && this.contains(removeButton)) {
        const list = removeButton.closest(
          "[data-fabrics-summary-list], [data-fabrics-mobile-summary-list]",
        );
        const removeButtons = list ? [...list.querySelectorAll("[data-summary-remove]")] : [];
        const removeIndex = removeButtons.indexOf(removeButton);
        const isMobileSummary = Boolean(
          removeButton.closest("[data-fabrics-mobile-summary-list]"),
        );
        const input = this.inputs.find(
          (item) => item.dataset.fabricHandle === removeButton.dataset.fabricHandle,
        );
        if (!input) return;

        input.checked = false;
        input.dispatchEvent(new Event("change", { bubbles: true }));

        const updatedList = this.querySelector(
          isMobileSummary
            ? "[data-fabrics-mobile-summary-list]"
            : "[data-fabrics-summary-list]",
        );
        const remainingButtons = updatedList
          ? [...updatedList.querySelectorAll("[data-summary-remove]")]
          : [];
        const nextButton = remainingButtons[Math.min(removeIndex, remainingButtons.length - 1)];

        if (nextButton) nextButton.focus({ preventScroll: true });
        else input.focus();
        return;
      }

      const tab = target.closest("[data-fabrics-tab]");
      if (tab && this.contains(tab)) this.setActiveTab(tab);
    }

    get selectedInputs() {
      return this.inputs.filter((input) => input.checked);
    }

    handleInputChange(input) {
      if (input.checked && this.selectedInputs.length > this.limit) {
        input.checked = false;
        this.announce(this.dataset.limitAnnouncement);
        return;
      }

      this.syncDrawerOption(input);
      this.renderSummary();
    }

    syncDrawerOptions() {
      this.inputs.forEach((input) => this.syncDrawerOption(input));
    }

    syncDrawerOption(source) {
      const handle = source.dataset.fabricHandle;
      if (!handle) return;

      const drawerOption = document.querySelector(
        `#drawer-fabrics input[name="fabrics"][data-fabric-handle="${CSS.escape(handle)}"]`,
      );

      if (!drawerOption || drawerOption.checked === source.checked) return;

      drawerOption.checked = source.checked;
      drawerOption.dispatchEvent(new Event("change", { bubbles: true }));
    }

    renderSummary() {
      if (!this.summary || !this.summaryList || !this.summaryTemplate) return;

      const selected = this.selectedInputs;
      this.summaryList.innerHTML = "";

      selected.forEach((input) => {
        const row = this.summaryTemplate.content.firstElementChild.cloneNode(true);
        const preview = input
          .closest("[data-fabric-card]")
          ?.querySelector(".fabrics__item-preview")
          ?.cloneNode(true);
        const previewTarget = row.querySelector("[data-summary-preview]");
        const name = row.querySelector("[data-summary-name]");
        const remove = row.querySelector("[data-summary-remove]");

        if (preview && previewTarget) previewTarget.appendChild(preview);
        if (name) name.textContent = input.value;

        if (remove) {
          const label = this.dataset.removeLabel || "";
          remove.dataset.fabricHandle = input.dataset.fabricHandle;
          remove.setAttribute(
            "aria-label",
            label.includes("{{ fabric }}")
              ? label.replace("{{ fabric }}", input.value)
              : `${label} ${input.value}`.trim(),
          );
        }

        this.summaryList.appendChild(row);
      });

      this.summary.hidden = selected.length === 0;
      this.renderMobileSummary(selected);
      if (this.promo) this.promo.hidden = selected.length > 0;
    }

    renderMobileSummary(selected) {
      if (
        !this.mobileTray ||
        !this.mobileSummaryList ||
        !this.mobileSummaryTemplate ||
        !this.mobileEmptyTemplate
      ) {
        return;
      }

      this.mobileSummaryList.innerHTML = "";

      selected.forEach((input) => {
        const slot = this.mobileSummaryTemplate.content.firstElementChild.cloneNode(true);
        const preview = input
          .closest("[data-fabric-card]")
          ?.querySelector(".fabrics__item-preview")
          ?.cloneNode(true);
        const previewTarget = slot.querySelector("[data-summary-preview]");
        const remove = slot.querySelector("[data-summary-remove]");

        if (preview && previewTarget) previewTarget.appendChild(preview);

        if (remove) {
          const label = this.dataset.removeLabel || "";
          remove.dataset.fabricHandle = input.dataset.fabricHandle;
          remove.setAttribute(
            "aria-label",
            label.includes("{{ fabric }}")
              ? label.replace("{{ fabric }}", input.value)
              : `${label} ${input.value}`.trim(),
          );
        }

        this.mobileSummaryList.appendChild(slot);
      });

      for (let index = selected.length; index < this.limit; index += 1) {
        this.mobileSummaryList.appendChild(
          this.mobileEmptyTemplate.content.firstElementChild.cloneNode(true),
        );
      }

      const hasSelection = selected.length > 0;
      this.mobileTray.hidden = !hasSelection;
      this.classList.toggle("main-fabrics--has-mobile-tray", hasSelection);
    }

    announce(message) {
      if (!this.status || !message) return;
      this.status.textContent = "";
      window.requestAnimationFrame(() => {
        this.status.textContent = message;
      });
    }

    requestTabUpdate() {
      if (this.scrollFrame) return;
      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.updateActiveTab();
      });
    }

    updateActiveTab() {
      if (!this.tabs.length || !this.categories.length) return;

      const marker = 130;
      let activeId = "all-fabrics";
      const visibleCategories = this.categories.filter(
        (category) => category.getClientRects().length > 0,
      );

      this.tabs.forEach((tab) => {
        if (tab.hash === "#all-fabrics") return;

        const category = this.categories.find((item) => tab.hash === `#${item.id}`);
        tab.hidden = !category || !visibleCategories.includes(category);
      });

      visibleCategories.forEach((category) => {
        if (category.getBoundingClientRect().top <= marker) activeId = category.id;
      });

      const activeTab = this.tabs.find((tab) => tab.hash === `#${activeId}`) || this.tabs[0];
      this.setActiveTab(activeTab);
    }

    setActiveTab(activeTab) {
      this.tabs.forEach((tab) => {
        if (tab === activeTab) tab.setAttribute("aria-current", "location");
        else tab.removeAttribute("aria-current");
      });
    }

    updateFilterExpanded(event, expanded) {
      if (event.detail?.id !== "drawer-filters" || !this.filterButton) return;

      this.filterButton.setAttribute("aria-expanded", String(expanded));
      if (!expanded) this.requestTabUpdate();
    }
  }

  customElements.define("fabric-samples-page", FabricSamplesPage);
}
